'use client';

import { c as _c } from "react/compiler-runtime";
import type { ProductCard } from '@repo/shopify';
import type { ReactNode } from 'react';
import { IntentPrefetchLink } from '@/components/ui/intent-prefetch-link';
import { ProductCarousel } from '../product/product-carousel';
import { ProductGridSkeleton } from '../product/product-grid-client';
import { getSectionSpacing } from './section-spacing';
interface ProductCarouselClientProps {
  products: ProductCard[];
  heading?: string;
  sectionId?: string;
  subheading?: string;
  showQuickAdd?: boolean;
  enableReviews?: boolean;
  moneyLocale?: string;
  ratings?: (number | null)[];
  reviewCounts?: (number | null)[];
  ctaButton?: {
    label?: string;
    url?: string;
  };
  sectionSpacing?: string;
  priorityImageCount?: number;
  isLoading?: boolean;
  loadingFallback?: ReactNode;
  showEmptyState?: boolean;
}
const DEFAULT_PRIORITY_IMAGE_COUNT = 2;
function ProductCarouselHeader(t0) {
  const $ = _c(8);
  const {
    heading,
    subheading,
    ctaButton,
    hasTextHeader,
    hasCta
  } = t0;
  if (!hasTextHeader && !hasCta) {
    return null;
  }
  let t1;
  if ($[0] !== ctaButton || $[1] !== hasCta || $[2] !== hasTextHeader || $[3] !== heading || $[4] !== subheading) {
    t1 = hasTextHeader ? <div>{subheading && <p className="text-xs font-semibold uppercase tracking-widest text-neutral-500">{subheading}</p>}<div className="flex flex-wrap items-end gap-x-6 gap-y-2">{heading && <h2 className="text-2xl font-bold tracking-tight md:text-3xl">{heading}</h2>}{hasCta && ctaButton?.url && <IntentPrefetchLink href={ctaButton.url} className="text-sm font-semibold text-neutral-900 underline underline-offset-4 transition-colors hover:text-neutral-600">{ctaButton.label}</IntentPrefetchLink>}</div></div> : hasCta && ctaButton?.url && <IntentPrefetchLink href={ctaButton.url} className="text-sm font-semibold text-neutral-900 underline underline-offset-4 transition-colors hover:text-neutral-600">{ctaButton.label}</IntentPrefetchLink>;
    $[0] = ctaButton;
    $[1] = hasCta;
    $[2] = hasTextHeader;
    $[3] = heading;
    $[4] = subheading;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== t1) {
    t2 = <div className="mb-8">{t1}</div>;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}

/**
 * Shared client-rendered product carousel section used by both published pages
 * and live preview so the block layout stays identical.
 */
export function ProductCarouselClient(t0) {
  const $ = _c(27);
  const {
    products,
    heading,
    sectionId,
    subheading,
    showQuickAdd: t1,
    enableReviews: t2,
    moneyLocale,
    ratings,
    reviewCounts,
    ctaButton,
    sectionSpacing,
    priorityImageCount: t3,
    isLoading: t4,
    loadingFallback,
    showEmptyState: t5
  } = t0;
  const showQuickAdd = t1 === undefined ? true : t1;
  const enableReviews = t2 === undefined ? false : t2;
  const priorityImageCount = t3 === undefined ? DEFAULT_PRIORITY_IMAGE_COUNT : t3;
  const isLoading = t4 === undefined ? false : t4;
  const showEmptyState = t5 === undefined ? false : t5;
  const hasTextHeader = Boolean(heading || subheading);
  const hasCta = Boolean(ctaButton?.label && ctaButton.url);
  const hasHeader = hasTextHeader || hasCta;
  if (products.length === 0 && !heading && !isLoading) {
    return null;
  }
  let t6;
  if ($[0] !== hasHeader || $[1] !== sectionSpacing) {
    t6 = getSectionSpacing(sectionSpacing, {
      hasHeader
    });
    $[0] = hasHeader;
    $[1] = sectionSpacing;
    $[2] = t6;
  } else {
    t6 = $[2];
  }
  let t7;
  if ($[3] !== ctaButton || $[4] !== hasCta || $[5] !== hasTextHeader || $[6] !== heading || $[7] !== subheading) {
    t7 = <ProductCarouselHeader heading={heading} subheading={subheading} ctaButton={ctaButton} hasTextHeader={hasTextHeader} hasCta={hasCta} />;
    $[3] = ctaButton;
    $[4] = hasCta;
    $[5] = hasTextHeader;
    $[6] = heading;
    $[7] = subheading;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== enableReviews || $[10] !== isLoading || $[11] !== loadingFallback || $[12] !== moneyLocale || $[13] !== priorityImageCount || $[14] !== products || $[15] !== ratings || $[16] !== reviewCounts || $[17] !== showEmptyState || $[18] !== showQuickAdd) {
    t8 = products.length > 0 ? <ProductCarousel products={products} showQuickAdd={showQuickAdd} priorityImageCount={priorityImageCount} enableReviews={enableReviews} moneyLocale={moneyLocale} ratings={ratings} reviewCounts={reviewCounts} /> : isLoading ? loadingFallback || <ProductGridSkeleton columns={4} count={4} /> : showEmptyState ? <p className="py-12 text-center text-neutral-500">No products to display.</p> : null;
    $[9] = enableReviews;
    $[10] = isLoading;
    $[11] = loadingFallback;
    $[12] = moneyLocale;
    $[13] = priorityImageCount;
    $[14] = products;
    $[15] = ratings;
    $[16] = reviewCounts;
    $[17] = showEmptyState;
    $[18] = showQuickAdd;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== t7 || $[21] !== t8) {
    t9 = <div className="container-page">{t7}{t8}</div>;
    $[20] = t7;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== sectionId || $[24] !== t6 || $[25] !== t9) {
    t10 = <section id={sectionId} className={t6}>{t9}</section>;
    $[23] = sectionId;
    $[24] = t6;
    $[25] = t9;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  return t10;
}